import { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';

/**
 * Wiederverwendbares One-Shot-Secret-Pattern.
 *
 * Use-Cases:
 *  - API-Key plaintext
 *  - Webhook-Signing-Secret
 *  - Generierte Initial-Passwörter
 *  - MFA-Backup-Codes
 *
 * Vertrag: Secret wird genau einmal angezeigt. Nach `onAcknowledged()`
 * unmount'et die Komponente und der Wert ist weg.
 */
export function OneShotSecretReveal({
  isOpen,
  secret,
  label,
  confirmPhrase = 'Ich habe das Secret sicher notiert',
  onAcknowledged,
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  // Vertrag: Parent muss die Komponente unmounten (`isOpen={false}` →
  // conditional render) wenn ein anderes Secret kommen soll. State-Reset
  // erfolgt über Remount — kein setState-in-useEffect-Hack nötig.

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      /* user copies manually */
    }
  };

  return (
    <Modal isOpen={isOpen} backdrop="static" keyboard={false} centered>
      <ModalHeader>{label} — einmalig anzeigen</ModalHeader>
      <ModalBody>
        <p className="text-muted small">
          Dieser Wert wird <strong>nur jetzt einmalig</strong> angezeigt. Nach dem Schließen
          kann er nicht wiederhergestellt werden.
        </p>
        <pre
          className="p-3 mb-3"
          style={{
            background: 'var(--bs-tertiary-bg)',
            border: '1px solid var(--bs-border-color)',
            borderRadius: 6,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            overflowX: 'auto',
          }}
        >
          {secret}
        </pre>
        <Button color="primary" outline onClick={copy}>
          {copied ? '✓ Kopiert' : '📋 In Zwischenablage'}
        </Button>
        <FormGroup check className="mt-4">
          <Input
            type="checkbox"
            id="oneShotAck"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <Label check for="oneShotAck">
            {confirmPhrase}
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" disabled={!acknowledged} onClick={onAcknowledged}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
}
